import * as styles from "./SectionArticleOrEventCards.module.scss";

import React, { useEffect, useState } from "react";
import { Button } from "../../../components/common";

import cx from "clsx";
import ArticleOrEventCard from "../../../components/SignPostCards/ArticleOrEventCard";
import { Chevron } from "../../../components/common/Icons";
import {
	checkIsFlat,
	getBiggestCardTextCount,
} from "./SectionArticleOrEventCards.utils";
export interface AccordionProps {
	items: DogsTrust.SignPostCardProps[];
	allowForButton?: boolean;
	showMoreLimit?: number;
	paginationLimit?: number;
	variant?: DogsTrust.ButtonVariant;
}

export const AllItems: React.FC<AccordionProps> = ({ items }) => {
	return (
		<div className={styles.cards}>
			{items.map((item, i) => (
				<ArticleOrEventCard
					key={item.title}
					label={item.label}
					{...item}
					isOrphan={checkIsFlat(items.length, i + 1)}
					biggestSiblingTextCount={getBiggestCardTextCount(items)}
				/>
			))}
		</div>
	);
};

export const Accordion: React.FC<AccordionProps> = ({ items }) => {
	const [showAll, setShowAll] = useState("show");
	const canShowAll = showAll !== "show";
	return (
		<>
			<div
				className={cx({
					[styles.cards]: canShowAll,
					[styles.collapsedcards]: !canShowAll,
				})}
			>
				{items.map((card) => (
					<ArticleOrEventCard key={card.title} {...card} />
				))}
			</div>
			<div className={styles.showmorearea}>
				{!canShowAll && <div className={styles.gradient} />}
				<button
					type="button"
					className={canShowAll ? styles.showless : styles.showmore}
					onClick={() =>
						setShowAll((prev) =>
							prev === "initial" ? "show" : prev === "show" ? "hide" : "show",
						)
					}
				>
					<div className={styles.text}>
						{`Show ${canShowAll ? "less" : "more"}`}{" "}
						<Chevron
							className={styles.chevron}
							height={"16px"}
							width={"16px"}
							rotation={canShowAll ? 0 : 180}
						/>
					</div>
				</button>
				{showAll !== "initial" && (
					<span className={styles.accessibilityalert} role="alert">
						{showAll === "show"
							? "All items are now visible"
							: "Some items are now hidden"}
					</span>
				)}
			</div>
		</>
	);
};

export const ItemLoader: React.FC<AccordionProps> = ({
	items,
	showMoreLimit,
	variant = "tertiary",
}) => {
	const [page, setPage] = useState(1);

	const calculateLoaded = (count: number) => `${(count / items.length) * 100}%`;

	if (items.length <= showMoreLimit) return <AllItems items={items} />;

	useEffect(() => {
		const scrollToTimeout = setTimeout(
			page > 1
				? () => {
						window.scrollBy({
							top: 0,
							behavior: "smooth",
						});
					}
				: () => {},
			100,
		);

		return () => {
			clearTimeout(scrollToTimeout);
		};
	}, [page]);

	return (
		<>
			<div className={styles.cards}>
				{items.slice(0, page * showMoreLimit).map((item, i) => (
					<ArticleOrEventCard
						key={item.title}
						label={item.label}
						{...item}
						isOrphan={checkIsFlat(items.length, i + 1)}
						biggestSiblingTextCount={getBiggestCardTextCount(items)}
					/>
				))}
			</div>
			<div className={styles.showmorearea}>
				<div className={styles.loadingbar}>
					<div
						className={styles.loadingbarfiller}
						style={{
							width: calculateLoaded(
								Math.min(items.length, page * showMoreLimit),
							),
						}}
					/>
				</div>
				<div className={styles.loadingarea}>
					<div className={styles.viewmoretext}>{`Viewed ${Math.min(
						items.length,
						page * showMoreLimit,
					)} of ${items.length} articles`}</div>
					{items.length > page * showMoreLimit && (
						<Button
							variant={variant}
							className={
								variant === "tertiary"
									? styles.showmorebutton
									: styles.showmorebuttonvariant
							}
							onClick={() => setPage((prev) => prev + 1)}
						>
							Show More
						</Button>
					)}
					{page > 1 && (
						<span className={styles.accessibilityalert} role="alert">
							{`Viewing page ${page} of ${
								Math.ceil(items.length / showMoreLimit) + 1
							}`}
						</span>
					)}
				</div>
			</div>
		</>
	);
};
